import { z } from 'zod';

/**
 * Interface themes.
 *
 * The chat surface is skinnable: the messaging logic, application state and
 * layout structure are independent of which visual language is in use. A theme
 * supplies design tokens and a small number of presentation variants; it never
 * changes how a message is sent, encrypted or stored.
 *
 * Identifiers describe the *look*, never a company. An earlier revision
 * reserved the names of six other messengers here, which put third-party marks
 * into the API, the database and the settings page for themes that were never
 * built. Naming a theme after someone else's product implies an association
 * this product does not have and is not entitled to claim; a theme that copies
 * a look can at least be described honestly, and a theme that borrows a name
 * cannot.
 *
 * Adding a theme is one identifier here, one entry in the web registry and one
 * token file. Nothing needs reserving in advance.
 */
export const interfaceThemeIds = ['classic_green'] as const;

export type InterfaceThemeId = (typeof interfaceThemeIds)[number];

/** The only theme that exists today, and the default for every account. */
export const DEFAULT_INTERFACE_THEME: InterfaceThemeId = 'classic_green';

/** Themes a user may actually select. Extend as each one is built. */
export const availableInterfaceThemes: readonly InterfaceThemeId[] = ['classic_green'];

export const interfaceThemeSchema = z.enum(interfaceThemeIds);

/**
 * Falls back to the default unless the theme is genuinely available, so a
 * preference written by a different build — including the retired identifiers
 * of the previous revision — leaves an older or newer client with a working
 * interface rather than none.
 */
export function resolveInterfaceTheme(value: unknown): InterfaceThemeId {
  const parsed = interfaceThemeSchema.safeParse(value);
  if (!parsed.success) return DEFAULT_INTERFACE_THEME;
  return availableInterfaceThemes.includes(parsed.data) ? parsed.data : DEFAULT_INTERFACE_THEME;
}

/**
 * Languages the product offers.
 *
 * One preference with two jobs: the language the interface is shown in, and the
 * language incoming messages are translated into. Translation still happens on
 * the participant's own device (concept §8) — this only chooses the target.
 *
 * Deliberately a closed list. On-device translation support varies by browser
 * and language pair, so offering everything would promise coverage the platform
 * may not have; the interface reports availability separately.
 */
export const supportedLanguages = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'it', label: 'Italian', native: 'Italiano' },
  { code: 'nl', label: 'Dutch', native: 'Nederlands' },
  { code: 'pt', label: 'Portuguese', native: 'Português' },
  { code: 'pl', label: 'Polish', native: 'Polski' },
  { code: 'hr', label: 'Croatian', native: 'Hrvatski' },
  { code: 'sr', label: 'Serbian', native: 'Srpski' },
  { code: 'cs', label: 'Czech', native: 'Čeština' },
  { code: 'tr', label: 'Turkish', native: 'Türkçe' },
  { code: 'ru', label: 'Russian', native: 'Русский' },
  { code: 'uk', label: 'Ukrainian', native: 'Українська' },
  { code: 'ar', label: 'Arabic', native: 'العربية' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'zh', label: 'Chinese', native: '中文' },
  { code: 'ja', label: 'Japanese', native: '日本語' },
  { code: 'ko', label: 'Korean', native: '한국어' },
] as const;

export type LanguageCode = (typeof supportedLanguages)[number]['code'];

export const languageSchema = z.enum(
  supportedLanguages.map((language) => language.code) as [LanguageCode, ...LanguageCode[]],
);

export const DEFAULT_LANGUAGE: LanguageCode = 'en';

/** Narrows a BCP-47 tag such as `en-GB` to a language we support. */
export function resolveLanguage(value: unknown): LanguageCode {
  if (typeof value !== 'string') return DEFAULT_LANGUAGE;
  const base = value.toLowerCase().split(/[-_]/)[0];
  const match = supportedLanguages.find((language) => language.code === base);
  return match ? match.code : DEFAULT_LANGUAGE;
}

/**
 * The best supported language from an `Accept-Language` header.
 *
 * Used where the browser is present but no preference has been stored — most
 * of all when sending the sign-in link, which goes to an address that may not
 * have an account yet and so has no preference to read.
 *
 * The header is already on every request, so reading it collects nothing new.
 * That matters: the visitor's language is deliberately never persisted
 * server-side (see `visitorLanguage.ts`), and this must not become the back
 * door that does it.
 *
 * Returns `null` rather than the default when nothing matches, so a caller can
 * tell "the browser asked for Dutch" from "the browser said nothing useful" and
 * fall back to its own better source.
 */
export function resolveAcceptLanguage(header: unknown): LanguageCode | null {
  if (typeof header !== 'string' || header.trim() === '') return null;

  const ranked = header
    .split(',')
    .map((part) => {
      const [tag, ...parameters] = part.trim().split(';');
      const quality = parameters
        .map((parameter) => /^\s*q=([\d.]+)\s*$/.exec(parameter))
        .find((match) => match !== null);
      return {
        tag: (tag ?? '').trim().toLowerCase(),
        // A malformed q is treated as absent, which means 1 per RFC 9110.
        quality: quality ? Number.parseFloat(quality[1]!) || 0 : 1,
      };
    })
    .filter((entry) => entry.tag !== '' && entry.quality > 0)
    // Stable sort: equal weights keep the order the browser listed them in.
    .sort((a, b) => b.quality - a.quality);

  for (const entry of ranked) {
    // `*` means "anything", which tells us nothing we did not already assume.
    if (entry.tag === '*') continue;
    const base = entry.tag.split(/[-_]/)[0];
    const match = supportedLanguages.find((language) => language.code === base);
    if (match) return match.code;
  }
  return null;
}
